import React, { useState } from "react";
import { Dialog, DialogContent, DialogTitle, Box, TextField, FormControl, InputLabel, Select, MenuItem, FormControlLabel, Checkbox, Button, Typography, Tooltip, IconButton, DialogActions } from "@mui/material";
import { Delete as DeleteIcon, Add as AddIcon } from "@mui/icons-material";
import { ApplicationQuestion, QuestionType } from "../../../../types/Applications";

// Edit Question Modal Props
interface EditQuestionModalProps {
    open: boolean;
    question: ApplicationQuestion | null;
    questions: ApplicationQuestion[];
    onClose: () => void;
    onSave: (question: ApplicationQuestion) => void;
}

// Using ApplicationQuestion from types now

// Edit Question Modal Component
const EditQuestionModal: React.FC<EditQuestionModalProps> = ({ open, question, questions, onClose, onSave }) => {
    const [editedQuestion, setEditedQuestion] = useState<ApplicationQuestion | null>(question);

    // Update local state when question prop changes
    React.useEffect(() => {
        setEditedQuestion(question);
    }, [question]);

    const handleSave = () => {
        if (editedQuestion) {
            onSave(editedQuestion);
        }
    };


    if (!editedQuestion) return null;
    
    // Determine if this is a new question (empty question text) or an existing one
    const isNewQuestion = !editedQuestion.question;

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle>{isNewQuestion ? 'Add Question' : 'Edit Question'}</DialogTitle>
            <DialogContent>
                <Box sx={{ pt: 1 }}>
                    <TextField
                        autoFocus
                        margin="dense"
                        label="Question Text"
                        fullWidth
                        value={editedQuestion.question}
                        onChange={(e) => setEditedQuestion({ ...editedQuestion, question: e.target.value })}
                        sx={{ mb: 3 }}
                    />

                    <FormControl fullWidth sx={{ mb: 3 }}>
                        <InputLabel id="question-type-label">Question Type</InputLabel>
                        <Select
                            labelId="question-type-label"
                            value={editedQuestion.type}
                            label="Question Type"
                            onChange={(e) => setEditedQuestion({ ...editedQuestion, type: e.target.value as QuestionType })}
                        >
                            <MenuItem value="short_answer">Short Answer</MenuItem>
                            <MenuItem value="paragraph">Paragraph</MenuItem>
                            <MenuItem value="multiple_choice">Multiple Choice</MenuItem>
                            <MenuItem value="multiple_select">Multiple Select</MenuItem>
                            <MenuItem value="date">Date</MenuItem>
                            <MenuItem value="time">Time</MenuItem>
                        </Select>
                    </FormControl>

                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={editedQuestion.required}
                                onChange={(e) => setEditedQuestion({ ...editedQuestion, required: e.target.checked })}
                            />
                        }
                        label="Required"
                    />

                    {/* Question role targeting */}
                    <Box sx={{ mt: 2 }}>
                        <Typography variant="subtitle2" sx={{ mb: 1 }}>Apply To</Typography>
                        <FormControl fullWidth size="small">
                            <Select
                                value={editedQuestion.for_role}
                                onChange={(e) => setEditedQuestion({
                                    ...editedQuestion,
                                    for_role: e.target.value as 'both' | 'mentor' | 'mentee',
                                    // Clear matching question ID if switching to 'both'
                                    matching_question_id: e.target.value === 'both' ? undefined : editedQuestion.matching_question_id
                                })}
                            >
                                <MenuItem value="both">Both Roles (Mentor & Mentee)</MenuItem>
                                <MenuItem value="mentor">Mentors Only</MenuItem>
                                <MenuItem value="mentee">Mentees Only</MenuItem>
                            </Select>
                        </FormControl>
                    </Box>

                    {/* Matching importance */}
                    <Box sx={{ mt: 2 }}>
                        <Typography variant="subtitle2" sx={{ mb: 1 }}>Matching Importance</Typography>
                        <FormControl fullWidth size="small">
                            <Select
                                value={editedQuestion.matching_importance}
                                onChange={(e) => setEditedQuestion({
                                    ...editedQuestion,
                                    matching_importance: e.target.value as 'none' | 'low' | 'medium' | 'high' | 'critical'
                                })}
                            >
                                <MenuItem value="none">None - Not Matched</MenuItem>
                                <MenuItem value="low">Low - Nice to have match</MenuItem>
                                <MenuItem value="medium">Medium - Somewhat important</MenuItem>
                                <MenuItem value="high">High - Very important</MenuItem>
                                <MenuItem value="critical">Critical - Must match</MenuItem>
                            </Select>
                        </FormControl>
                    </Box>

                    {/* Matching question selector - only show if question is role-specific */}
                    {editedQuestion.for_role !== 'both' && (
                        <Box sx={{ mt: 2 }}>
                            <Typography variant="subtitle2" sx={{ mb: 1 }}>
                                {editedQuestion.for_role === 'mentor' ? 'Matching Mentee Question' : 'Matching Mentor Question'}
                            </Typography>
                            <FormControl fullWidth size="small">
                                <Select
                                    value={editedQuestion.matching_question_id || ''}
                                    displayEmpty
                                    onChange={(e) => setEditedQuestion({
                                        ...editedQuestion,
                                        matching_question_id: e.target.value as string
                                    })}
                                >
                                    <MenuItem value="">None (Create new matching question)</MenuItem>
                                    {questions
                                        .filter((q: ApplicationQuestion) =>
                                            q.id !== editedQuestion.id &&
                                            q.for_role !== 'both' &&
                                            q.for_role !== editedQuestion.for_role
                                        )
                                        .map((q: ApplicationQuestion) => (
                                            <MenuItem key={q.id} value={q.id}>{q.question}</MenuItem>
                                        ))
                                    }
                                </Select>
                            </FormControl>
                            <Typography variant="caption" sx={{ display: 'block', mt: 0.5, color: 'text.secondary' }}>
                                Select an existing question from the other role that this should be matched with,
                                or leave empty to create a new matching question automatically.
                            </Typography>
                        </Box>
                    )}

                    {(editedQuestion.type === 'multiple_choice' || editedQuestion.type === 'multiple_select') && (
                        <Box sx={{ mt: 2 }}>
                            <Typography variant="subtitle2" sx={{ mb: 1 }}>Options</Typography>
                            {(editedQuestion.options || []).map((option, index) => (
                                <Box key={index} sx={{ display: 'flex', mb: 1 }}>
                                    <TextField
                                        fullWidth
                                        size="small"
                                        value={option}
                                        onChange={(e) => {
                                            const newOptions = [...(editedQuestion.options || [])];
                                            newOptions[index] = e.target.value;
                                            setEditedQuestion({ ...editedQuestion, options: newOptions });
                                        }}
                                    />
                                    <Tooltip title="Remove option">
                                        <IconButton
                                            size="small"
                                            onClick={() => {
                                                const newOptions = (editedQuestion.options || []).filter((_, i) => i !== index);
                                                setEditedQuestion({ ...editedQuestion, options: newOptions });
                                            }}
                                        >
                                            <DeleteIcon fontSize="small" />
                                        </IconButton>
                                    </Tooltip>
                                </Box>
                            ))}
                            <Tooltip title="Add a new answer option">
                                <Button
                                    variant="text"
                                    startIcon={<AddIcon />}
                                    onClick={() => {
                                        const newOptions = [...(editedQuestion.options || []), ''];
                                        setEditedQuestion({ ...editedQuestion, options: newOptions });
                                    }}
                                    sx={{ mt: 1 }}
                                >
                                    Add Option
                                </Button>
                            </Tooltip>
                        </Box>
                    )}
                </Box>
            </DialogContent>
            <DialogActions>
                <Tooltip title="Cancel editing">
                    <Button onClick={onClose} color="primary">
                        Cancel
                    </Button>
                </Tooltip>
                <Tooltip title="Save changes">
                    <Button onClick={handleSave} color="primary" variant="contained">
                        Save
                    </Button>
                </Tooltip>
            </DialogActions>
        </Dialog>
    );
};

export default EditQuestionModal;